import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

export default function Login() {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [forgotOpen, setForgotOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // A bejelentkezés az AuthContext-en át
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        // A szerverről kapott user role elmentése
        const userRole = result.user.role;

        // Átirányítás a felhasználó szerepe alapján
        if (userRole === 'Rendszergazda') {
          navigate('/rendszergazda-panel');
        } else if (userRole === 'Fodrász') {
          navigate('/fodrasz-panel');
        } else {
          navigate('/foglalas');
        }
      } else {
        // A szerver hibaüzenet kiírása
        setError(result.error);
      }
    } catch (err) {
      setError('Hálózati hiba történt. Ellenőrizd, hogy fut-e a Node.js szerver!');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!resetEmail.trim()) {
      setResetMessage('Kérlek, add meg az email címet!');
      return;
    }

    setResetLoading(true);
    setResetMessage('');

    try {
      //Az elfelejtett jelszó kérés email küldés-sel történik
      const response = await fetch(`/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: resetEmail }),
      });

      const data = await response.json();

      // A felhasználónak a szerver által adott eredményt vagy hibaüzenetet jelenítjük meg.
      if (response.ok && data.success) {
        setResetMessage(data.message || 'A jelszó-helyreállítási email elküldve!');
      } else {
        setResetMessage(data.message || 'Hiba történt a kérés feldolgozása során.');
      }
    } catch (err) {
      setResetMessage('Hálózati hiba történt. Ellenőrizd a szervert!');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className=" min-h-screen flex items-center justify-center p-4 bg-transparent font-sans transition-colors">
      <div className="max-w-md w-full bg-transparent rounded-[0.5rem] shadow-none p-5 sm:p-10 border border-gray-300 dark:border-gray-700 transition-colors">
        
        <div className="text-center mb-10">
          <h1 className="text-4xl font-black text-gray-900 dark:text-white tracking-tighter uppercase ">
            Bejelentkezés
          </h1>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-rose-50 dark:bg-rose-900/30 border-l-4 border-rose-500 dark:border-rose-700 rounded-xl text-rose-700 dark:text-rose-400 text-sm font-bold transition-colors">
            ⚠️ {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">
              Email cím
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full p-4 bg-gray-50 dark:bg-gray-700 border-2 border-gray-100 dark:border-gray-600 rounded-xl focus:border-blue-500 dark:focus:border-blue-400 outline-none font-bold transition-all dark:text-white"
              placeholder="felhasznalo@gmail.com"
            />
          </div>

          <div>
            <label className="block text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">
              Jelszó
            </label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full p-4 bg-gray-50 dark:bg-gray-700 border-2 border-gray-100 dark:border-gray-600 rounded-xl focus:border-blue-500 dark:focus:border-blue-400 outline-none font-bold transition-all dark:text-white"
              placeholder="••••••••"
            />
          </div>

          <div className="-mb-1 mt-1 text-left">
            <button
              type="button"
              onClick={() => setForgotOpen(true)}
              className="text-sm font-medium  text-blue-600/80 dark:text-blue-300/90 hover:text-blue-700 dark:hover:text-blue-200 hover:underline transition-all"
            >
              Elfelejtetted jelszavad?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 shadow-lg shadow-purple-500/20 text-white py-5 rounded-xl font-black uppercase tracking-widest shadow-xl hover:shadow-2xl transition-all active:scale-95 disabled:opacity-50 mt-4"
          >
            {loading ? "Azonosítás..." : "Bejelentkezés"}
          </button>
        </form>

        <div className="mt-8 pt-8 border-t border-gray-300 dark:border-gray-700 text-center transition-colors">
          <p className="text-gray-400 dark:text-gray-500 font-bold text-xs uppercase mb-4">Még nem jártál nálunk?</p>
          <Link 
            to="/regisztracio"
            className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-orange-500 dark:from-pink-500 dark:to-orange-500 text-sm font-black uppercase tracking-tighter hover:underline"
          >
            Regisztrálj most →
          </Link>
        </div>
      </div>

      {forgotOpen && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="w-full max-w-md rounded-[2rem] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-2xl p-6">
            <div className="flex justify-between items-center mb-5">
              <h2 className="w-full text-center text-2xl font-black text-gray-900 dark:text-white">Elfelejtett jelszó</h2>
              <button
                type="button"
                onClick={() => {

                  setForgotOpen(false);
                  setResetMessage('');
                  setResetEmail('');
                }}
                className="text-xl text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ×
              </button>
            </div>

            <p className="w-full text-center text-sm text-gray-600 dark:text-gray-300 mb-4">
              Add meg a regisztrált email címed, és elküldjük a jelszó-helyreállítási linket.
            </p>

            <label className="block text-[10px] font-black text-gray-400 dark:text-gray-500 uppercase tracking-widest mb-2 ml-1">
              Email cím
            </label>
            <input
              type="email"
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              className="w-full p-4 bg-gray-50 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-blue-500 dark:focus:border-blue-400 outline-none font-bold transition-all dark:text-white"
              placeholder="pelda@email.com"
            />

            {resetMessage && (
              <div className="mt-4 p-3 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 rounded-xl text-emerald-700 dark:text-emerald-300 text-sm font-semibold">
                {resetMessage}
              </div>
            )}

            <button
              type="button"
              disabled={resetLoading}
              onClick={handleForgotPassword}
              className="mt-5 w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-black uppercase tracking-widest shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
            >
              {resetLoading ? 'Új jelszó kérése...' : 'Új jelszó kérése'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}